import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  createStyles,
  Fade,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { darken, fade, lighten } from '@material-ui/core/styles/colorManipulator';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useEffect, useRef, useState } from 'react';
import CopyResultsWidget from '../../CopyResultsWidget/CopyResultsWidget';
import { DNSLookUpTest } from '../../NetworkTestWidget/Tests/Tests.model';
import { TestResults, TestWarnings } from '../../types';
import { getDNSlookupAlertVariant } from '../../utils';
import { CheckDNSLookupResults } from './CheckDNSLookupResults/CheckDNSLookupResults';

const useStyles = makeStyles((theme: Theme) => {
  const getBackgroundColor = theme.palette.type === 'light' ? lighten : darken;

  return createStyles({
    tableHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1em',
    },
    table: {
      tableLayout: 'fixed',
      borderTop: `1px solid ${theme.palette.divider}`,
      '& td, & th': {
        width: '260px',
        borderRight: `1px solid
      ${
          // Same implementation as material-ui's table borderBottom.
          theme.palette.type === 'light'
            ? lighten(fade(theme.palette.divider, 1), 0.88)
            : darken(fade(theme.palette.divider, 1), 0.68)
          }`,
      },
      '& td:last-child, & th:last-child': {
        borderRight: 'none',
      },
      '& th, & td:first-child': {
        fontWeight: 'bold',
      },
    },
    tableBorder: {
      border: '1px solid rgba(0, 0, 0, 0.12)',
    },
    tableCellContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& svg': {
        fill: theme.palette.success.main,
      },
    },
    tableCellDnsSuccess: {
      justifyContent: 'center',
      '& svg': {
        fill: theme.palette.success.main,
      },
    },
    tableCellDnsFailure: {
      justifyContent: 'center',
      '& svg': {
        fill: theme.palette.error.main,
      },
    },
    headerContent: {
      display: 'flex',
      '& p': {
        fontWeight: 'bold',
        marginLeft: '12px',
      },
    },
    tableHeaderCell: {
      textAlign: 'center',
    },
    bestEdgeCell: {
      background: getBackgroundColor(theme.palette.success.main, 0.9),
    },
    [TestWarnings.warn]: {
      background: getBackgroundColor(theme.palette.warning.main, 0.9),
    },
    [TestWarnings.error]: {
      background: getBackgroundColor(theme.palette.error.main, 0.9),
    },
  });
});

const DNSLookupTestResults = ({ results }: { results?: TestResults }) => {
  const classes = useStyles();

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const accordionSummaryRef = useRef<any>(null);

  useEffect(() => {
    if (results && getDNSlookupAlertVariant(results) === 'error') {
      setIsAccordionOpen(true);
    }
  }, [results]);

  if (!results) return null;

  const { results: testResults } = results;

  const scrollAccordionIntoView = () => {
    if (isAccordionOpen && accordionSummaryRef.current) {
      setTimeout(() => {
        accordionSummaryRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }

  const toggleAccordion = () => setIsAccordionOpen((isOpen: boolean) => !isOpen);

  return (
    <Fade in={true} timeout={1000}>
      <Box paddingTop={4} className="dns-tests-results-container">
        <Paper elevation={3} ref={accordionSummaryRef}>
          <div className={classes.tableHeader}>
            <Typography variant="h5" paragraph>
              DNS Lookup Test
            </Typography>
            <CopyResultsWidget results={results} />
          </div>

          <CheckDNSLookupResults results={results} />

          <Box padding={2}>
            <Accordion
              TransitionProps={{
                addEndListener: scrollAccordionIntoView,
              }}
              expanded={isAccordionOpen}
            >
              <AccordionSummary
                onClick={toggleAccordion}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>Show Detailed Results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer className={classes.tableBorder}>
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell}>Domain URL</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Reachable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {testResults.dnsLookupTest?.individualTestResults.map((testResult: DNSLookUpTest) => (
                        <TableRow key={testResult.url}>
                          <TableCell>
                            <div className={classes.tableCellContent}>{testResult.url}</div>
                          </TableCell>
                          <TableCell>
                            <div
                              className={`${classes.tableCellContent} ${testResult.reachable ? classes.tableCellDnsSuccess : classes.tableCellDnsFailure
                                }`}
                            >
                              {testResult.reachable ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export { DNSLookupTestResults };
